import { createTheme } from '@mui/material/styles';
import { darkColors } from './colors';

type AppColors = typeof darkColors;

export function createAppTheme(mode: 'dark' | 'light', colors: AppColors) {
  const displayFont = '"Orbitron", sans-serif';
  const bodyFont = '"Exo 2", "Roboto", "Helvetica", "Arial", sans-serif';
  const monoFont = '"Share Tech Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: { main: colors.accent.cyan },
      secondary: { main: colors.accent.purple },
      success: { main: colors.accent.green },
      error: { main: colors.accent.red },
      warning: { main: colors.accent.orange },
      background: {
        default: colors.bg.primary,
        paper: colors.bg.secondary,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
        disabled: colors.text.muted,
      },
      divider: colors.border.default,
    },
    typography: {
      fontFamily: bodyFont,
      h1: {
        fontFamily: displayFont,
        fontWeight: 800,
        letterSpacing: 0,
        textTransform: 'uppercase' as const,
      },
      h2: {
        fontFamily: displayFont,
        fontWeight: 800,
        letterSpacing: 0,
        textTransform: 'uppercase' as const,
      },
      h3: {
        fontFamily: displayFont,
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'uppercase' as const,
      },
      h4: {
        fontFamily: displayFont,
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'uppercase' as const,
      },
      h5: {
        fontFamily: displayFont,
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'uppercase' as const,
      },
      h6: {
        fontFamily: displayFont,
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'uppercase' as const,
      },
      button: {
        fontFamily: displayFont,
        fontWeight: 600,
        letterSpacing: 0,
        textTransform: 'uppercase' as const,
      },
      body1: {
        fontFamily: bodyFont,
        fontWeight: 400,
      },
      body2: {
        fontFamily: bodyFont,
        fontWeight: 400,
      },
      caption: {
        fontFamily: bodyFont,
        fontWeight: 400,
      },
      overline: {
        fontFamily: monoFont,
        letterSpacing: 0,
        textTransform: 'uppercase' as const,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            minWidth: '320px',
            backgroundColor: colors.bg.primary,
            backgroundImage: isDark
              ? 'linear-gradient(180deg, rgba(185,28,28,0.10) 0%, rgba(8,8,14,0) 260px)'
              : 'linear-gradient(180deg, rgba(12,12,20,0.035) 0%, rgba(250,250,250,0) 320px)',
            color: colors.text.primary,
            fontFamily: bodyFont,
            lineHeight: 1.7,
            overflowX: 'hidden',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            scrollbarWidth: 'thin',
            scrollbarColor: `${colors.border.default} ${colors.bg.primary}`,
          },
          '#root': {
            minHeight: '100vh',
            isolation: 'isolate',
          },
          '::selection': {
            background: colors.accent.cyanGlow,
            color: colors.text.primary,
          },
          a: {
            color: 'inherit',
            textDecoration: 'none',
          },
          '::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '::-webkit-scrollbar-track': {
            background: colors.bg.primary,
          },
          '::-webkit-scrollbar-thumb': {
            background: colors.border.default,
            borderRadius: '4px',
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: colors.border.strong,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.bg.overlay,
            borderBottom: `1px solid ${colors.border.subtle}`,
            backdropFilter: 'blur(24px) saturate(1.8)',
            WebkitBackdropFilter: 'blur(24px) saturate(1.8)',
            boxShadow: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            minHeight: 38,
            transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.35s cubic-bezier(0.4, 0, 0.2, 1), background 0.35s cubic-bezier(0.4, 0, 0.2, 1), color 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          contained: {
            position: 'relative',
            overflow: 'hidden',
            background: colors.accent.cyan,
            color: '#ffffff',
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: 0,
            border: 'none',
            clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
            boxShadow: 'none',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.18), transparent 48%)',
              opacity: 0,
              transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              pointerEvents: 'none',
            },
            '&:hover': {
              background: colors.accent.cyan,
              boxShadow: `0 12px 40px ${colors.accent.cyanGlow}`,
              transform: 'translateY(-2px)',
              '&::before': {
                opacity: 1,
              },
            },
            '&.Mui-disabled': {
              background: colors.bg.tertiary,
              color: colors.text.muted,
            },
          },
          outlined: {
            borderColor: colors.border.default,
            color: colors.text.secondary,
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.035)' : 'rgba(255, 255, 255, 0.44)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
            '&:hover': {
              color: colors.accent.cyan,
              borderColor: colors.border.accent,
              backgroundColor: colors.accent.cyanGlow,
              transform: 'translateY(-2px)',
            },
          },
          text: {
            color: colors.text.secondary,
            '&:hover': {
              color: colors.accent.cyan,
              backgroundColor: colors.accent.cyanGlow,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            transition: 'color 0.2s ease, background-color 0.2s ease, transform 0.2s ease',
            '&:hover': {
              color: colors.accent.cyan,
              backgroundColor: colors.accent.cyanGlow,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: colors.bg.secondary,
            borderColor: colors.border.subtle,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: colors.text.primary,
            borderBottom: `1px solid ${colors.border.subtle}`,
            fontFamily: bodyFont,
            padding: '14px 16px',
          },
          head: {
            color: colors.text.secondary,
            fontFamily: displayFont,
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            fontSize: '0.75rem',
            letterSpacing: 0,
            backgroundColor: colors.bg.tertiary,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: `${colors.bg.tertiary} !important`,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              color: colors.text.primary,
              backgroundColor: isDark ? colors.bg.tertiary : 'rgba(255, 255, 255, 0.64)',
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              '& fieldset': {
                borderColor: colors.border.default,
                transition: 'border-color 0.2s ease',
              },
              '&:hover fieldset': {
                borderColor: colors.border.accent,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.accent.cyan,
                boxShadow: `0 0 0 2px ${colors.accent.cyanGlow}`,
              },
            },
            '& .MuiInputLabel-root': {
              color: colors.text.secondary,
              '&.Mui-focused': {
                color: colors.accent.cyan,
              },
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: colors.text.secondary,
            fontFamily: displayFont,
            textTransform: 'none' as const,
            transition: 'color 0.2s ease',
            '&.Mui-selected': {
              color: colors.accent.cyan,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: colors.accent.cyan,
            height: 2,
            boxShadow: `0 0 8px ${colors.accent.cyanGlow}`,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.bg.overlay,
            border: `1px solid ${colors.border.default}`,
            backdropFilter: 'blur(20px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
            borderRadius: '8px',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: colors.text.primary,
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: colors.accent.cyanGlow,
            },
          },
        },
      },
      MuiModal: {
        styleOverrides: {
          root: {
            '& .MuiBackdrop-root': {
              backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(4px)',
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: colors.bg.surface,
            borderRadius: '4px',
            '& .MuiLinearProgress-bar': {
              background: colors.gradient.primary,
              borderRadius: '4px',
            },
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: colors.accent.cyan,
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            color: colors.accent.cyan,
          },
          mark: {
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: colors.accent.cyan,
            boxShadow: `0 0 8px ${colors.accent.cyanGlow}`,
          },
          markLabel: {
            color: colors.text.secondary,
            fontFamily: displayFont,
            fontSize: '0.75rem',
          },
          thumb: {
            color: colors.accent.cyan,
            boxShadow: `0 0 10px ${colors.accent.cyanGlow}`,
          },
          track: {
            background: colors.gradient.primary,
            border: 'none',
          },
          rail: {
            color: colors.bg.surface,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: colors.text.secondary,
            '&.Mui-checked': {
              color: colors.accent.cyan,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: displayFont,
            borderRadius: '6px',
          },
        },
      },
    },
  });
}

// Default theme for backward compatibility
export default createAppTheme('dark', darkColors);
